<template>
  <div class="w-full bg-white p-6">
    <div v-if="contentLoading && isBlocked" class="max-w-2xl mx-auto">
      <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="font-semibold text-red-700">Доступ к лабораторной работе заблокирован.</p>
        <p class="text-sm text-red-600 mt-1">Обратитесь к модератору для сброса попыток и уточнения условий повторного прохождения.</p>
      </div>
    </div>
    <div v-else-if="contentLoading && noActiveApplication" class="max-w-2xl mx-auto">
      <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="font-semibold text-red-700">Доступ к лабораторной работе закрыт.</p>
        <p class="text-sm text-red-600 mt-1">Нет активной одобренной заявки на курс. Подайте заявку повторно.</p>
      </div>
    </div>
    <!-- Результат после теста -->
    <div v-else-if="contentLoading && !canTesting" class="max-w-2xl mx-auto">
      <div class="bg-white rounded-lg shadow-lg p-8 text-center">
        <h1 class="text-3xl font-bold text-gray-800 mb-4">Лабораторная работа завершена</h1>
        <div class="text-5xl font-bold mb-4" :style="{ color: testResult >= 50 ? '#10b981' : '#ef4444' }">
          {{ testResult.toFixed(1) }}%
        </div>
        <p class="text-lg text-gray-600 mb-8">
          {{ testResult >= 50 ? 'Работа пройдена успешно!' : 'Результат ниже минимально допустимого (50%)' }}
        </p>
        <button
          @click="goBack"
          class="bg-blue-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-600 transition"
        >
          Вернуться
        </button>
      </div>
    </div>

    <!-- Карточка для начала теста -->
    <div v-else-if="contentLoading && canTesting && !testing" class="max-w-2xl mx-auto">
      <div class="bg-white rounded-lg shadow-lg p-8">
        <h1 class="text-3xl font-bold text-gray-800 mb-4">Лабораторная работа</h1>
        <div class="mb-6">
          <p class="text-lg text-gray-600 mb-4">
            Вам предстоит решить <strong class="text-2xl text-blue-600">{{ questionsData.length }}</strong>
            {{ questionsData.length === 1 ? 'задачу' : 'задач' }}
          </p>
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p class="text-sm text-yellow-800">
              ℹ️ Минимальный балл для прохождения: <strong>50%</strong>
            </p>
          </div>
        </div>
        <button
          @click="startTesting"
          class="w-full bg-blue-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-600 transition text-lg"
        >
          Начать лабораторную работу
        </button>
      </div>
    </div>

    <!-- Основной интерфейс теста -->
    <div v-else-if="contentLoading && canTesting && testing && currentQuestion" class="max-w-3xl mx-auto">
      <!-- Progress bar -->
      <div class="mb-6">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm font-semibold text-gray-700">
            Задача {{ currentQuestionIndex + 1 }} из {{ questionsData.length }}
          </span>
          <span class="text-sm text-gray-500">{{ Math.round((currentQuestionIndex + 1) / questionsData.length * 100) }}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-3">
          <div
            class="bg-blue-600 h-3 rounded-full transition-all duration-300"
            :style="{ width: `${(currentQuestionIndex + 1) / questionsData.length * 100}%` }"
          ></div>
        </div>
      </div>

      <!-- Question Card -->
      <div class="bg-white rounded-lg shadow-lg p-8 mb-6">
        <!-- Question text -->
        <h2 class="text-2xl font-bold text-gray-800 mb-4">{{ currentQuestion.question }}</h2>

        <!-- Question image -->
        <img
          v-if="currentQuestion.image"
          :src="currentQuestion.image"
          :alt="currentQuestion.question"
          class="max-w-full h-auto rounded-lg mb-6 max-h-80"
        />

        <!-- Answer options -->
        <div class="space-y-4 mb-6">
          <div
            v-for="answer in currentQuestion.question_answers"
            :key="answer.id"
            class="border border-gray-200 rounded-lg p-4"
          >
            <div class="flex items-start">
              <input
                type="checkbox"
                :id="`answer-${answer.id}`"
                :checked="isAnswerSelected(answer.id)"
                @change="toggleAnswer(answer.id)"
                class="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div class="ml-4 flex-1">
                <label :for="`answer-${answer.id}`" class="text-gray-700 cursor-pointer font-medium">
                  {{ answer.answer }}
                </label>
                <img
                  v-if="answer.image"
                  :src="answer.image"
                  :alt="answer.answer"
                  class="max-w-sm h-auto rounded mt-2 max-h-40"
                />
              </div>
            </div>
            <!-- Explanation for answer -->
            <div v-if="answer.explanation && showResult" class="mt-3 ml-9 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
              <p class="text-blue-700 text-sm">
                <strong>💡 Пояснение:</strong> {{ answer.explanation }}
              </p>
            </div>
          </div>
        </div>

        <!-- Show result feedback -->
        <div v-if="showResult" class="mb-6">
          <div v-if="isAnswerCorrect" class="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <p class="text-green-800 font-semibold">✅ Правильно!</p>
          </div>
          <div v-else class="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <p class="text-red-800 font-semibold mb-2">❌ Неправильно</p>
            <div v-if="currentQuestion.explanation" class="text-red-700 text-sm mt-2">
              <strong>Объяснение:</strong> {{ currentQuestion.explanation }}
            </div>
          </div>
        </div>
      </div>

      <!-- Control buttons -->
      <div class="flex gap-3 justify-between">
        <button
          v-if="currentQuestionIndex > 0"
          @click="previousQuestion"
          class="px-6 py-3 bg-gray-400 text-white font-bold rounded-lg hover:bg-gray-500 transition"
          :disabled="showResult && currentQuestionIndex === questionsData.length - 1"
        >
          ← Предыдущий
        </button>
        <div v-else></div>

        <button
          v-if="!showResult"
          @click="submitAnswer"
          class="px-6 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition"
        >
          Проверить ответ
        </button>

        <button
          v-else-if="currentQuestionIndex < questionsData.length - 1"
          @click="nextQuestion"
          class="px-6 py-3 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 transition"
        >
          Далее →
        </button>

        <button
          v-else
          @click="finishTest"
          :disabled="isSubmitting"
          class="px-6 py-3 bg-green-500 text-white font-bold rounded-lg hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-500"
        >
          <span v-if="!isSubmitting">Завершить тест</span>
          <span v-else class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Обработка...
          </span>
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else class="max-w-2xl mx-auto">
      <div class="bg-white rounded-lg shadow-lg p-8 text-center">
        <p class="text-gray-600">Загрузка...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { API_CLIENT, HEADERS_TOKEN_JSON } from '@/const/uri';
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  emits: ['active'],
  data() {
    return {
      contentLoading: false,
      canTesting: false,
      isBlocked: false,
      noActiveApplication: false,
      testing: false,
      questionsData: [],
      answers: {},
      testResult: null,
      currentQuestionIndex: 0,
      showResult: false,
      isSubmitting: false,
    };
  },
  computed: {
    currentQuestion() {
      return this.questionsData[this.currentQuestionIndex] || null;
    },
    isAnswerCorrect() {
      if (!this.currentQuestion) return false;
      const correctAnswerIds = new Set(
        this.currentQuestion.question_answers
          .filter(a => a.is_correct)
          .map(a => a.id)
      );
      const userAnswerIds = new Set(this.answers[this.currentQuestion.id] || []);
      return (
        correctAnswerIds.size === userAnswerIds.size &&
        [...correctAnswerIds].every(id => userAnswerIds.has(id))
      );
    },
  },
  mounted() {
    this.loadData();
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteUpdate(to, from, next) {
    this.loadData();
    next();
  },
  watch: {
    testing(val) {
      this.setBeforeUnload(val);
      this.$emit('active', val);
    },
  },
  methods: {
    ...mapActions(['toggleAlert']),

    onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = '';
    },
    setBeforeUnload(active) {
      if (active) {
        window.addEventListener('beforeunload', this.onBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
      }
    },

    startTesting() {
      this.testing = true;
      this.currentQuestionIndex = 0;
      this.showResult = false;
    },

    isAnswerSelected(answerId) {
      return (this.answers[this.currentQuestion.id] || []).includes(answerId);
    },

    toggleAnswer(answerId) {
      if (!this.answers[this.currentQuestion.id]) {
        this.answers[this.currentQuestion.id] = [];
      }
      const answers = this.answers[this.currentQuestion.id];
      const index = answers.indexOf(answerId);
      if (index > -1) {
        answers.splice(index, 1);
      } else {
        answers.push(answerId);
      }
    },

    submitAnswer() {
      this.showResult = true;
    },

    nextQuestion() {
      if (this.currentQuestionIndex < this.questionsData.length - 1) {
        this.currentQuestionIndex++;
        this.showResult = false;
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
        this.showResult = false;
      }
    },

    async finishTest() {
      if (this.isSubmitting) return; // Защита от двойного клика
      this.isSubmitting = true;
      try {
        await this.submitAnswersToServer();
      } finally {
        this.isSubmitting = false;
      }
    },

    async loadData() {
      try {
        await axios({
          method: 'GET',
          url: API_CLIENT.clientLabQuestions + this.$route.params.labWorkUUID + '/',
          headers: HEADERS_TOKEN_JSON(),
        }).then((e) => {
          this.questionsData = e.data.data.questions;
          this.createAnswers(this.questionsData);
          this.contentLoading = true;
          this.canTesting = e.data.data.can_testing;
          this.isBlocked = e.data.data.is_blocked || false;
          this.noActiveApplication = e.data.data.no_active_application || false;
          this.testResult = e.data.data.result;
        });
      } catch (error) {
        console.error('Ошибка при загрузке:', error);
        this.toggleAlert({
          message: 'Ошибка при загрузке данных',
          isError: true,
        });
      }
    },

    createAnswers(data) {
      const obj = {};
      data.forEach(el => {
        obj[el.id] = [];
      });
      this.answers = obj;
    },

    async submitAnswersToServer() {
      try {
        const response = await axios({
          method: 'POST',
          url: API_CLIENT.clientLabQuestions + this.$route.params.labWorkUUID + '/answers/',
          headers: HEADERS_TOKEN_JSON(),
          data: {
            answers: this.answers,
          },
        });

        this.testing = false;
        this.canTesting = false;
        this.testResult = response.data.data.result.value;
        const isBlock = response.data.data.result.is_block;

        this.toggleAlert({
          message: isBlock ? 'Ваш балл меньше нужного (50%)' : 'Лабораторная работа пройдена успешно!',
          isError: isBlock,
        });
      } catch (error) {
        console.error('Ошибка при отправке ответов:', error);
        this.toggleAlert({
          message: 'Ошибка при отправке ответов',
          isError: true,
        });
      }
    },

    goBack() {
      this.$router.go(-1);
    },
  },
  props: {
    labWorkUUID: {},
  },
};
</script>
